import { useEffect, useState } from 'react';
import { ArrowRight, Loader2, MailOpen } from 'lucide-react';
import type { MemberMe } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Logo, Button, Card, Input } from '@techsofit/ui';
import { requestMemberOtp, verifyMemberLink, verifyMemberOtp } from '../lib/memberApi';
import { isIntlPhone, normalizeIntlPhone } from '../lib/phone';

/**
 * Passwordless member sign-in. Today the code is delivered by email only. Phone (WhatsApp/SMS)
 * delivery is off until we have a deliverable channel — flip PHONE_LOGIN_ENABLED here AND the
 * backend `techsofit.member-otp.phone-channel` together to restore it.
 */
const PHONE_LOGIN_ENABLED = false;

/**
 * Phones often reload this page when the member hops to their inbox and back. We remember the
 * "code sent" step (and the contact) for as long as the code is valid so they land back on the
 * code field — not on an empty email field that would issue a fresh code and void the old one.
 */
const PENDING_KEY = 'tsf.member-otp.pending';
const CODE_TTL_MS = 10 * 60 * 1000;

interface Pending {
  contact: string;
  sentAt: number;
  next: '/checkin' | '/me';
}

function readPending(next: '/checkin' | '/me'): Pending | null {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Pending;
    if (!p?.contact || typeof p.sentAt !== 'number') return null;
    if (Date.now() - p.sentAt > CODE_TTL_MS) {
      window.localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return p.next === next ? p : null;
  } catch {
    return null;
  }
}

function writePending(p: Pending) {
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch {
    /* private mode — fine, we just lose the refresh safety net */
  }
}

function clearPending() {
  try {
    window.localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/** Pull `?lt=` off the URL (the email's one-tap token) and clean the address bar. */
function takeLinkToken(): string | null {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('lt');
  if (!token) return null;
  url.searchParams.delete('lt');
  window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
  return token;
}

/**
 * Passwordless member sign-in (email → code, or one tap from the email). Reused by /me, the
 * QR check-in page and the login chooser. `context` tunes the copy and where the email button lands.
 */
export function MemberLogin({
  onAuthed,
  onBack,
  context = 'dashboard',
}: {
  onAuthed: (me: MemberMe) => void;
  onBack?: () => void;
  context?: 'dashboard' | 'checkin';
}) {
  const next: '/checkin' | '/me' = context === 'checkin' ? '/checkin' : '/me';
  const [pending] = useState(() => readPending(next));
  const [linkToken] = useState(() => takeLinkToken());
  const [step, setStep] = useState<'link' | 'contact' | 'code'>(linkToken ? 'link' : pending ? 'code' : 'contact');
  const [contact, setContact] = useState(pending?.contact ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEmail = contact.includes('@');
  const channel = isEmail ? 'email' : 'WhatsApp';
  const sendContact = isEmail ? contact.trim() : normalizeIntlPhone(contact.trim());

  // One-tap from the email: verify the link token, then hand over the session.
  useEffect(() => {
    if (!linkToken) return;
    let active = true;
    verifyMemberLink(linkToken)
      .then((me) => {
        if (!active) return;
        clearPending();
        onAuthed(me);
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err instanceof ApiClientError
            ? err.message
            : 'That sign-in link has expired — enter your email and we’ll send a new code.',
        );
        // Fall back to the code step if we still have a live one, else start over.
        setStep(pending ? 'code' : 'contact');
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken]);

  function validate(c: string): string | null {
    if (!c) return PHONE_LOGIN_ENABLED ? 'Enter your phone number or email.' : 'Enter your email.';
    if (!PHONE_LOGIN_ENABLED && !c.includes('@')) {
      return 'Enter the email you were registered with.';
    }
    if (PHONE_LOGIN_ENABLED && !isEmail && !isIntlPhone(c)) {
      return 'Use international format, e.g. +263 77 123 4567 — or your email.';
    }
    return null;
  }

  async function sendCode() {
    const c = contact.trim();
    const invalid = validate(c);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { found } = await requestMemberOtp(sendContact, next);
      if (!found) {
        setError(
          isEmail
            ? "We couldn't find an account with that email. Try the email you were registered with, or ask your gym or studio to add you as a member."
            : "We couldn't find an account with that number. Try your email, or ask your gym or studio to add you as a member.",
        );
        return;
      }
      writePending({ contact: c, sentAt: Date.now(), next });
      setCode('');
      setStep('code');
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (!code.trim()) {
      setError('Enter the code.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const me = await verifyMemberOtp(sendContact, code.trim());
      clearPending();
      onAuthed(me);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "That code didn't work.");
      setBusy(false);
    }
  }

  function startOver() {
    clearPending();
    setStep('contact');
    setCode('');
    setError(null);
  }

  const title = context === 'checkin' ? 'Check in' : 'Your dashboard';

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-4">
      <Card className="w-full max-w-sm p-6">
        <Logo />
        <h1 className="mt-5 font-display text-display-sm text-ink">{title}</h1>

        {step === 'link' ? (
          <div className="mt-6 flex flex-col items-center gap-3 py-6 text-center">
            <Loader2 className="size-6 animate-spin text-ink" aria-hidden />
            <p className="text-sm text-muted">Signing you in…</p>
          </div>
        ) : step === 'contact' ? (
          <>
            <p className="mt-1 text-sm text-muted">
              {context === 'checkin'
                ? 'Enter the email you were registered with. We’ll send a code and bring you straight back here.'
                : PHONE_LOGIN_ENABLED
                  ? "Enter the phone number or email you were registered with — we'll send a sign-in code."
                  : "Enter the email you were registered with — we'll send a sign-in code."}
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <Input
                label={PHONE_LOGIN_ENABLED ? 'Phone or email' : 'Email'}
                type={PHONE_LOGIN_ENABLED ? 'text' : 'email'}
                inputMode={PHONE_LOGIN_ENABLED ? 'text' : 'email'}
                autoComplete="email"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={PHONE_LOGIN_ENABLED ? '+263 77 000 0000 or you@email.com' : 'you@email.com'}
                hint={
                  PHONE_LOGIN_ENABLED
                    ? contact.trim()
                      ? channel === 'email'
                        ? "We'll email your code."
                        : "We'll send your code on WhatsApp."
                      : 'A phone gets a code on WhatsApp; an email gets an emailed code.'
                    : "We'll email your sign-in code."
                }
                onKeyDown={(e) => e.key === 'Enter' && sendCode()}
              />
              {error && <p className="text-sm font-medium text-bad-ink">{error}</p>}
              <Button onClick={sendCode} loading={busy} block>
                Send code <ArrowRight className="size-4" aria-hidden />
              </Button>
              {onBack && (
                <button type="button" onClick={onBack} className="text-sm text-muted hover:text-ink">
                  ← Back
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-muted">
              Enter the 6-digit code we sent to your {channel}: <span className="font-medium text-ink">{contact}</span>.
            </p>
            <div className="mt-4 flex items-start gap-2.5 rounded-control bg-surface px-3.5 py-3 text-left">
              <MailOpen className="mt-0.5 size-4 shrink-0 text-ink-soft" aria-hidden />
              <p className="text-xs text-ink-soft">
                In a hurry? The email also has a <span className="font-semibold text-ink">one-tap button</span> that signs
                you in — no typing. Your code stays valid if this page reloads.
              </p>
            </div>
            <div className="mt-4 flex flex-col gap-3">
              <Input
                label="Code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                onKeyDown={(e) => e.key === 'Enter' && verify()}
              />
              {error && <p className="text-sm font-medium text-bad-ink">{error}</p>}
              <Button onClick={verify} loading={busy} block>
                {context === 'checkin' ? 'Continue to check-in' : 'Sign in'}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={sendCode} disabled={busy} className="text-muted hover:text-ink disabled:opacity-50">
                  Resend code
                </button>
                <button type="button" onClick={startOver} className="text-muted hover:text-ink">
                  {PHONE_LOGIN_ENABLED ? 'Different phone or email' : 'Different email'}
                </button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
